import type { StateStore } from 'oidc-client-ts';

/**
 * The `userStore` for `UserManager`. Access and ID tokens live in memory; only
 * the refresh token is persisted, and only to `sessionStorage`.
 *
 * Plan 8.4.2 states the rule and the reason: a stolen refresh token that
 * survives tab close is materially worse than an extra login. `oidc-client-ts`
 * keeps the whole `User` record in one store, so a plain `WebStorageStateStore`
 * would put the ID token, which is the bearer credential for the read API,
 * into web storage next to it.
 *
 * This store splits the record:
 *
 *   - the complete `User` string is held in an instance scoped Map, so a tab
 *     that never reloads never touches storage;
 *   - `sessionStorage` receives a redacted copy with `access_token` and
 *     `id_token` stripped and `expires_at` forced to zero, keeping only
 *     `refresh_token`, `profile`, `scope` and `token_type`.
 *
 * After a reload `getUser()` therefore returns a user that reads as expired but
 * carries a refresh token, which is exactly the input
 * `UserManager.signinSilent()` needs to mint a fresh pair with no redirect and
 * no re-prompt (acceptance A3.11). `profile` is retained deliberately:
 * `signinSilent` compares `profile.sub` against the refreshed identity.
 *
 * Only user records pass through here. PKCE verifiers and signin state go to a
 * separate, prefixed `WebStorageStateStore`, because `State.clearStaleState`
 * deletes every key a state store reports that it cannot parse as state.
 */

const PREFIX = 'zs.oidc.user.';

interface StoredUser {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  session_state?: string | null;
  token_type?: string;
  scope?: string;
  profile?: unknown;
  expires_at?: number;
}

/**
 * Storage access throws rather than returning null in several real situations:
 * Safari private mode quota, a partitioned third party context, and enterprise
 * policy lockdown. None of them should take the dashboard down, so every call
 * degrades to the in memory copy and the user simply logs in again next load.
 */
function probeSessionStorage(): Storage | null {
  try {
    const probe = `${PREFIX}probe`;
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Strip the bearer credentials, keep what a refresh needs. Returns `null` when
 * there is no refresh token, because in that case persisting anything would be
 * storing an ID token for no benefit.
 */
export function redactUserRecord(value: string): string | null {
  let parsed: StoredUser;
  try {
    parsed = JSON.parse(value) as StoredUser;
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  if (typeof parsed.refresh_token !== 'string' || parsed.refresh_token === '') {
    return null;
  }
  return JSON.stringify({
    refresh_token: parsed.refresh_token,
    session_state: parsed.session_state ?? null,
    token_type: parsed.token_type,
    scope: parsed.scope,
    profile: parsed.profile,
    // Zero reads as expired to `User.expires_in`, which is what drives the
    // silent renew on boot.
    expires_at: 0,
  });
}

export class SplitTokenStore implements StateStore {
  private readonly memory = new Map<string, string>();
  private readonly storage: Storage | null;

  constructor(storage: Storage | null = probeSessionStorage()) {
    this.storage = storage;
  }

  async set(key: string, value: string): Promise<void> {
    this.memory.set(key, value);
    if (!this.storage) return;

    const persisted = redactUserRecord(value);
    try {
      if (persisted === null) this.storage.removeItem(PREFIX + key);
      else this.storage.setItem(PREFIX + key, persisted);
    } catch {
      // Quota or policy. The in memory copy still serves this tab.
    }
  }

  async get(key: string): Promise<string | null> {
    const inMemory = this.memory.get(key);
    if (inMemory !== undefined) return inMemory;
    if (!this.storage) return null;
    try {
      return this.storage.getItem(PREFIX + key);
    } catch {
      return null;
    }
  }

  async remove(key: string): Promise<string | null> {
    const existing = await this.get(key);
    this.memory.delete(key);
    if (this.storage) {
      try {
        this.storage.removeItem(PREFIX + key);
      } catch {
        // Nothing useful to do: the in memory copy is already gone.
      }
    }
    return existing;
  }

  async getAllKeys(): Promise<string[]> {
    const keys = new Set(this.memory.keys());
    if (this.storage) {
      try {
        for (let i = 0; i < this.storage.length; i += 1) {
          const key = this.storage.key(i);
          if (key !== null && key.startsWith(PREFIX)) {
            keys.add(key.slice(PREFIX.length));
          }
        }
      } catch {
        // Fall through to the in memory keys.
      }
    }
    return [...keys];
  }
}
